import { useState } from "react";
import { useTranslation } from "react-i18next";
import { XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { TruncatedText } from "@/components/ui/truncated-text";

interface CancelInspectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inspection: { id: string; checklistName: string } | null;
  onCancelled?: () => void;
}

export function CancelInspectionDialog({
  open,
  onOpenChange,
  inspection,
  onCancelled,
}: CancelInspectionDialogProps) {
  const { t } = useTranslation();

  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCancel = async () => {
    if (!inspection) return;

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.rpc("cancel_inspection", {
        _inspection_id: inspection.id,
        _reason: reason || undefined,
      });

      if (error) throw error;

      const cancelledDefects =
        (data as { cancelled_defect_count?: number } | null)?.cancelled_defect_count ?? 0;

      toast.success(t("inspections.cancelDialog.successTitle"), {
        description:
          cancelledDefects > 0
            ? t("inspections.cancelDialog.successDescriptionWithDefects", {
                count: cancelledDefects,
              })
            : t("inspections.cancelDialog.successDescription"),
      });

      setReason("");
      onOpenChange(false);
      onCancelled?.();
    } catch (error) {
      console.error("Error cancelling inspection:", error);
      toast.error(t("common.error"), {
        description: t("inspections.cancelDialog.errorDescription"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setReason("");
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md w-[calc(100%-2rem)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-muted-foreground" />
            {t("inspections.cancelDialog.title")}
          </DialogTitle>
          <DialogDescription>{t("inspections.cancelDialog.description")}</DialogDescription>
        </DialogHeader>

        {inspection && (
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-md min-w-0">
              <TruncatedText text={inspection.checklistName} className="font-medium" maxLines={2} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">{t("inspections.cancelDialog.reason")}</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("inspections.cancelDialog.reasonPlaceholder")}
                rows={3}
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
            {t("common.cancel")}
          </Button>
          <Button variant="destructive" onClick={handleCancel} disabled={isSubmitting}>
            {isSubmitting ? t("common.saving") : t("inspections.cancelDialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
